import { featureEquals, type Features } from "byzantine/src/Feature";
import {
  ACCOUNT_PERMISSION_OPTIONS,
  ALL,
  MODEL_STR,
  type AccountPermissionGroup,
  type AccountPermissionParent,
  type Role,
} from "byzantine/src/Role";
import {
  buildAccountPermissionState,
  type AccountPermissionState,
} from "./RoleFormUtils";

export type RoleAccountEntry<T> = {
  account: T;
  state: AccountPermissionState;
};

/**
 * The accounts a role reaches, each with its access and account-level permissions. Works for
 * converted roles (stored `account_permissions`) and legacy roles (`account_permissions: null`,
 * projected from the flat grants). Accounts the role has no access to are left out.
 */
export const getRoleAccountEntries = <T extends { id: string | number }>(
  role: Role,
  accounts: T[]
): RoleAccountEntry<T>[] => {
  const stateByAccount = buildAccountPermissionState(role, accounts);
  return accounts.flatMap((account) => {
    const state = stateByAccount[String(account.id)];
    return state ? [{ account, state }] : [];
  });
};

const signature = ({ access, permissions }: AccountPermissionState) =>
  access === "view_only"
    ? "view_only"
    : `${access}:${[...permissions].sort().join(",")}`;

/** True when there are at least two accounts and every one has the same access and permissions. */
export const hasSamePermissionsAcrossAccounts = (
  entries: RoleAccountEntry<unknown>[]
): boolean =>
  entries.length > 1 &&
  new Set(entries.map((entry) => signature(entry.state))).size === 1;

/** Extra detail shown under a parent row, after the templates note. */
export type AccountPermissionDetail =
  | { kind: "modifier"; modelStr: string }
  | { kind: "importAch" }
  | { kind: "importWire" };

export type AccountPermissionRow = {
  /** Stable key, also used to pick the label. */
  key: string;
  /** Model string whose label names the row (rows without a parent). */
  modelStr?: string;
  parent?: AccountPermissionParent;
  /** Whether the granted permissions include the version with and/or without templates. */
  templates?: "both" | "templatesOnly";
  details: AccountPermissionDetail[];
};

export type AccountPermissionRowGroups = Record<
  AccountPermissionGroup,
  AccountPermissionRow[]
>;

// Parents whose children are a base and a template version of one payment type. Any other child
// of such a parent (FedNow, reversals) is a modifier of that row.
const TEMPLATE_PARENTS: Partial<
  Record<AccountPermissionParent, { base: string; template: string }>
> = {
  sendAchPayments: {
    base: MODEL_STR.ACH_PAYMENT,
    template: MODEL_STR.ACH_PAYMENT_VIA_TEMPLATE,
  },
  collectAchPayments: {
    base: MODEL_STR.ACH_PAYMENT_COLLECT,
    template: MODEL_STR.ACH_PAYMENT_COLLECT_VIA_TEMPLATE,
  },
  sendDomesticWires: {
    base: MODEL_STR.DOMESTIC_WIRE_TRANSACTION,
    template: MODEL_STR.DOMESTIC_WIRE_VIA_TEMPLATES,
  },
  sendInternationalWires: {
    base: MODEL_STR.INTERNATIONAL_WIRE,
    template: MODEL_STR.INTERNATIONAL_WIRE_VIA_TEMPLATES,
  },
};

const WIRE_IMPORT = MODEL_STR.DOMESTIC_WIRE_IMPORT;

/**
 * Rows to show for one account's granted permissions, in catalog order and grouped like the
 * new/edit role page: one row per parent ("Send ACH payments") with its children summarized
 * underneath, and one row for each permission without a parent.
 */
export const getAccountPermissionRows = (
  permissions: string[],
  features?: Features
): AccountPermissionRowGroups => {
  const granted = new Set(permissions);
  const groups: AccountPermissionRowGroups = {
    moneyMovement: [],
    manageTemplates: [],
  };
  const rowsByParent = new Map<string, AccountPermissionRow>();

  ACCOUNT_PERMISSION_OPTIONS.forEach((option) => {
    if (!granted.has(option.modelStr)) return;
    // Admin holds the whole catalog, so leave out what the institution doesn't offer.
    if (features && !option.isAvailable(features)) return;
    const { parent, group, modelStr } = option;
    if (!parent) {
      groups[group].push({ key: modelStr, modelStr, details: [] });
      return;
    }
    let row = rowsByParent.get(parent);
    if (!row) {
      row = { key: parent, parent, details: [] };
      rowsByParent.set(parent, row);
      groups[group].push(row);
    }
    const pair = TEMPLATE_PARENTS[parent];
    if (pair) {
      if (modelStr === pair.template) {
        row.templates = granted.has(pair.base) ? "both" : "templatesOnly";
      } else if (modelStr !== pair.base) {
        row.details.push({ kind: "modifier", modelStr });
      }
    } else {
      const detail: AccountPermissionDetail =
        modelStr === WIRE_IMPORT
          ? { kind: "importWire" }
          : { kind: "importAch" };
      if (!row.details.some((existing) => existing.kind === detail.kind)) {
        row.details.push(detail);
      }
    }
  });
  return groups;
};

export const countAccountPermissions = (
  permissions: string[],
  features?: Features
): number => {
  const { moneyMovement, manageTemplates } = getAccountPermissionRows(
    permissions,
    features
  );
  return moneyMovement.length + manageTemplates.length;
};

// Role-level permissions in display order, each with the feature flags it needs.
const ROLE_LEVEL_PERMISSIONS: { modelStr: string; flags: string[] }[] = [
  { modelStr: MODEL_STR.RECIPIENT, flags: [] },
  { modelStr: MODEL_STR.CARD, flags: ["cards"] },
  { modelStr: MODEL_STR.PAYMENT, flags: ["bill_pay"] },
  { modelStr: MODEL_STR.PAYEE, flags: ["bill_pay"] },
  { modelStr: MODEL_STR.EXTERNAL_ACCOUNT, flags: ["ach"] },
  { modelStr: MODEL_STR.ZELLE, flags: ["zelle"] },
  { modelStr: MODEL_STR.DOCUMENTS, flags: ["statements"] },
  { modelStr: MODEL_STR.ACH_IMPORT_FILE_DOWNLOADS, flags: ["ach_file_import"] },
  {
    modelStr: MODEL_STR.BANKING_API_TOKEN_ALLOWED_SCOPES,
    flags: ["business_banking_api"],
  },
];

const APPROVALS: { modelStr: string; flags: string[] }[] = [
  { modelStr: MODEL_STR.DUAL_APPROVAL_REQUEST, flags: ["dual_approval"] },
  {
    modelStr: MODEL_STR.APPROVE_ACH_TEMPLATES,
    flags: ["ach_templates", "dual_approval"],
  },
];

const isWriteGrant = (permission: API.Permission) =>
  permission.operation === "write" || permission.operation === "*";

/** A grant on every model, which is how Admin holds everything. */
const hasGlobalGrant = (role: Role) =>
  role.permissions.some(
    (permission) => permission.model_str === ALL && isWriteGrant(permission)
  );

/**
 * Role-level permissions and approvals the role holds, as model strings in display order, limited
 * to what the institution offers. Account-level permissions are shown per account, and account
 * access itself is not a checkbox.
 */
export const getRoleLevelModelStrs = (
  role: Role,
  features: Features = {}
): { permissions: string[]; approvals: string[] } => {
  const granted = new Set(
    role.permissions
      .filter(
        (p) =>
          isWriteGrant(p) &&
          (p.model_str !== MODEL_STR.EXTERNAL_ACCOUNT || p.uuid === ALL)
      )
      .map((p) => p.model_str)
  );
  const holdsAll = hasGlobalGrant(role);
  const pick = (list: { modelStr: string; flags: string[] }[]) =>
    list
      .filter(
        ({ modelStr, flags }) =>
          (holdsAll || granted.has(modelStr)) &&
          flags.every((flag) => featureEquals(features, flag))
      )
      .map(({ modelStr }) => modelStr);
  return {
    permissions: pick(ROLE_LEVEL_PERMISSIONS),
    approvals: pick(APPROVALS),
  };
};
